import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import * as syncProtocol from "y-protocols/sync";
import * as awarenessProtocol from "y-protocols/awareness";
import { encoding, decoding } from "lib0";
import { MonacoBinding } from "y-monaco";
import { buildWsUrl } from "../lib/ws-url";

/**
 * Connects a Monaco editor instance to a server-side Y.Doc over the
 * Vor WebSocket endpoint at /api/projects/:id/files/:path/ws.
 *
 * Reconnects with exponential backoff on close. Persistence is the
 * server's job (debounced writes to ProjectSource); the client just
 * mirrors a Y.Text into the Monaco buffer via y-monaco's binding.
 */

const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;

interface UseYjsMonacoArgs {
  projectId: string | null;
  filePath: string | null;
  /** Real Monaco editor instance — typed loosely to avoid pulling in
   * the full `monaco-editor` types here. */
  editor: unknown | null;
}

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";

export function useYjsMonaco({ projectId, filePath, editor }: UseYjsMonacoArgs) {
  const [state, setState] = useState<ConnState>("idle");

  // Held across renders to clean up properly on unmount / arg change.
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!projectId || !filePath || !editor) {
      setState("idle");
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    let ws: WebSocket | null = null;
    let doc: Y.Doc | null = null;
    let awareness: awarenessProtocol.Awareness | null = null;
    let binding: MonacoBinding | null = null;

    function teardown() {
      cancelled = true;
      if (backoffTimer) clearTimeout(backoffTimer);
      try {
        binding?.destroy();
      } catch {
        // ignore
      }
      try {
        awareness?.destroy();
      } catch {
        // ignore
      }
      try {
        doc?.destroy();
      } catch {
        // ignore
      }
      if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
    }
    cleanupRef.current = teardown;

    function connect() {
      if (cancelled) return;
      setState("connecting");

      const url = buildWsUrl(`/api/projects/${projectId}/files/${encodeURIComponent(filePath!)}/ws`);

      doc = new Y.Doc();
      const yText = doc.getText("content");
      awareness = new awarenessProtocol.Awareness(doc);

      ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";

      ws.addEventListener("open", () => {
        if (cancelled) return;
        attempt = 0;
        setState("open");

        // Bind once the connection is up; otherwise the server's
        // initial sync step 1 might race with our local insert.
        // y-monaco wants the model + a Set of editors; coerce via any
        // since we keep the editor typed loosely up top.
        const e = editor as { getModel: () => unknown };
        binding = new MonacoBinding(
          yText,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          e.getModel() as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          new Set([editor as any]),
          awareness!,
        );

        // Send sync step 1 ourselves so the server knows our state.
        const encoder = encoding.createEncoder();
        encoding.writeVarUint(encoder, MESSAGE_SYNC);
        syncProtocol.writeSyncStep1(encoder, doc!);
        ws!.send(encoding.toUint8Array(encoder));

        // Forward local Y.Doc updates over the wire.
        const onUpdate = (update: Uint8Array, origin: unknown) => {
          if (origin === ws) return; // came from the wire
          const enc = encoding.createEncoder();
          encoding.writeVarUint(enc, MESSAGE_SYNC);
          syncProtocol.writeUpdate(enc, update);
          if (ws && ws.readyState === ws.OPEN) {
            ws.send(encoding.toUint8Array(enc));
          }
        };
        doc!.on("update", onUpdate);

        // Awareness — broadcast cursor / selection.
        const onAwarenessUpdate = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }) => {
          const changed = added.concat(updated).concat(removed);
          const enc = encoding.createEncoder();
          encoding.writeVarUint(enc, MESSAGE_AWARENESS);
          encoding.writeVarUint8Array(
            enc,
            awarenessProtocol.encodeAwarenessUpdate(awareness!, changed),
          );
          if (ws && ws.readyState === ws.OPEN) {
            ws.send(encoding.toUint8Array(enc));
          }
        };
        awareness!.on("update", onAwarenessUpdate);
      });

      ws.addEventListener("message", (e: MessageEvent<ArrayBuffer>) => {
        if (!doc) return;
        try {
          const buf = new Uint8Array(e.data);
          const decoder = decoding.createDecoder(buf);
          const messageType = decoding.readVarUint(decoder);
          if (messageType === MESSAGE_SYNC) {
            const enc = encoding.createEncoder();
            encoding.writeVarUint(enc, MESSAGE_SYNC);
            syncProtocol.readSyncMessage(decoder, enc, doc, ws);
            if (encoding.length(enc) > 1 && ws && ws.readyState === ws.OPEN) {
              ws.send(encoding.toUint8Array(enc));
            }
          } else if (messageType === MESSAGE_AWARENESS) {
            awarenessProtocol.applyAwarenessUpdate(
              awareness!,
              decoding.readVarUint8Array(decoder),
              ws,
            );
          }
        } catch {
          // ignore malformed
        }
      });

      ws.addEventListener("close", () => {
        if (cancelled) return;
        setState("closed");
        binding?.destroy();
        binding = null;
        // Reconnect with backoff: 0.5s, 1s, 2s, 4s … capped at 16s.
        attempt = Math.min(attempt + 1, 6);
        const delay = Math.min(16_000, 500 * 2 ** (attempt - 1));
        backoffTimer = setTimeout(connect, delay);
      });

      ws.addEventListener("error", () => {
        if (cancelled) return;
        setState("error");
      });
    }

    connect();

    return () => {
      teardown();
    };
  }, [projectId, filePath, editor]);

  return { state };
}
